import type {
  TestingInstructionApplyMode,
  TestingInstructionPipeline,
} from './types';

type AppliesToInput = {
  applyMode: TestingInstructionApplyMode;
  pipelines: TestingInstructionPipeline[];
  endpointGlobs: string[];
  description: string | null;
};

type AppliesToLabels = {
  allPipelines: string;
  pipelinesList: (pipelines: string[]) => string;
  always: string;
  autoAttached: (args: { pipelinesLabel: string; globCount: number }) => string;
  agentRequested: (description: string | null) => string;
  manual: string;
  pipelineLabel: (pipeline: TestingInstructionPipeline) => string;
};

const DEFAULT_PIPELINE_LABELS: Record<TestingInstructionPipeline, string> = {
  SIGNAL_GENERATION: 'Signal generation',
  SECURITY_TEST_CASE: 'Security test case',
  ATTACK: 'Attack',
  DISCOVERY: 'Discovery',
  PROBING: 'Probing',
};

export const DEFAULT_APPLIES_TO_LABELS: AppliesToLabels = {
  allPipelines: 'All pipelines',
  pipelinesList: (pipelines) => pipelines.join(', '),
  always: 'Always applied',
  autoAttached: ({ pipelinesLabel, globCount }) =>
    globCount > 0
      ? `${pipelinesLabel} · ${globCount} endpoint pattern${globCount === 1 ? '' : 's'}`
      : `${pipelinesLabel} · matching endpoints`,
  agentRequested: (description) =>
    description?.trim()
      ? `When agent requests: ${description.trim()}`
      : 'When agent requests',
  manual: 'Co-pilot @mention only',
  pipelineLabel: (pipeline) => DEFAULT_PIPELINE_LABELS[pipeline],
};

const formatPipelinesLabel = ({
  pipelines,
  labels,
}: {
  pipelines: TestingInstructionPipeline[];
  labels: AppliesToLabels;
}): string => {
  if (pipelines.length === 0) {
    return labels.allPipelines;
  }

  return labels.pipelinesList(
    pipelines.map((pipeline) => labels.pipelineLabel(pipeline)),
  );
};

export const formatAppliesToSummary = (
  input: AppliesToInput,
  labels: AppliesToLabels = DEFAULT_APPLIES_TO_LABELS,
): string => {
  const pipelinesLabel = formatPipelinesLabel({
    pipelines: input.pipelines,
    labels,
  });

  switch (input.applyMode) {
    case 'ALWAYS':
      return input.pipelines.length === 0
        ? `${labels.always} · ${labels.allPipelines}`
        : `${labels.always} · ${pipelinesLabel}`;
    case 'AUTO_ATTACHED':
      return labels.autoAttached({
        pipelinesLabel,
        globCount: input.endpointGlobs.length,
      });
    case 'AGENT_REQUESTED':
      return labels.agentRequested(input.description);
    case 'MANUAL':
      return labels.manual;
  }
};
